import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLACEHOLDER_PHONE, PLACEHOLDER_EMAIL } from '../lib/contactDefaults';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Save,
  MapPin,
  Building,
  Mail,
  Phone,
  User,
  FileText,
  ClipboardList,
  Plus,
  HardHat
} from 'lucide-react';

const PROJECT_TYPE_OPTIONS = [
  { value: 'DEV', label: 'Development' },
  { value: 'CON', label: 'Construction' }
];

const PROJECT_SOURCE_OPTIONS = [
  { value: 'Main Contractor', label: 'Main Contractor' },
  { value: 'Residential Client', label: 'Residential Client' }
];

const STATUS_OPTIONS = ['Live', 'Complete', 'Archive'];

export function CreateProject() {
  const [formData, setFormData] = useState({
    project_type: '',
    project_source: 'Main Contractor',
    contact_id: '',
    architect_id: '',
    client_name: '',
    address_line1: '',
    town_city: 'Northampton',
    postcode: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    notes: '',
    status: 'Live'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [contractors, setContractors] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [loadingArchitects, setLoadingArchitects] = useState(false);
  
  // New Contact/Architect dialogs
  const [showNewContactDialog, setShowNewContactDialog] = useState(false);
  const [showNewArchitectDialog, setShowNewArchitectDialog] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    company: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    address: '',
    postcode: ''
  });
  const [newArchitect, setNewArchitect] = useState({
    name: '',
    company: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    address: '',
    postcode: ''
  });
  
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Fetch contractors when project source is Main Contractor
  const fetchContractors = useCallback(async () => {
    setLoadingContractors(true);
    try {
      const response = await api.get('/contacts?category=Contractor');
      setContractors(response.data);
    } catch (error) {
      console.error('Failed to fetch contractors:', error);
    } finally {
      setLoadingContractors(false);
    }
  }, [api]);

  // Fetch architects
  const fetchArchitects = useCallback(async () => {
    setLoadingArchitects(true);
    try {
      const response = await api.get('/contacts?category=Architect');
      setArchitects(response.data);
    } catch (error) {
      console.error('Failed to fetch architects:', error);
    } finally {
      setLoadingArchitects(false);
    }
  }, [api]);

  // Load contractors (only if source defaults to Main Contractor) and architects ONCE on mount.
  // Subsequent source changes are handled in handleProjectSourceChange, so we intentionally
  // omit formData.project_source from the dependency array.
  useEffect(() => {
    if (formData.project_source === 'Main Contractor') {
      fetchContractors();
    }
    fetchArchitects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchContractors, fetchArchitects]);

  // Redirect if not admin
  if (!isAdmin) {
    navigate('/projects');
    return null;
  }

  const handleProjectSourceChange = (value) => {
    setFormData(prev => ({ ...prev, project_source: value, contact_id: '' }));
    if (value === 'Main Contractor') {
      fetchContractors();
    }
  };

  const handleContractorChange = (contactId) => {
    setFormData(prev => ({ ...prev, contact_id: contactId }));
    // If a contractor is selected, populate some fields from the contact
    if (contactId && contactId !== 'none') {
      const contractor = contractors.find(c => c.id === contactId);
      if (contractor) {
        setFormData(prev => ({
          ...prev,
          contact_id: contactId,
          client_name: contractor.company || contractor.name,
          telephone: contractor.telephone || prev.telephone,
          email: contractor.email || prev.email
        }));
      }
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name.trim()) {
      toast.error('Please enter a contact name');
      return;
    }
    
    try {
      const response = await api.post('/contacts', {
        name: newContact.name.trim(),
        company: newContact.company.trim() || null,
        telephone: newContact.telephone.trim() || null,
        email: newContact.email.trim() || null,
        address: newContact.address.trim() || null,
        postcode: newContact.postcode.trim().toUpperCase() || null,
        category: 'Contractor',
        marketing_consent: 'Awaiting'
      });
      
      toast.success('Contractor added successfully');
      setShowNewContactDialog(false);
      setNewContact({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
      
      // Refresh contractors list and select the new one
      await fetchContractors();
      handleContractorChange(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contractor');
    }
  };

  const handleCreateArchitect = async () => {
    if (!newArchitect.name.trim()) {
      toast.error('Please enter an architect name');
      return;
    }
    
    try {
      const response = await api.post('/contacts', {
        name: newArchitect.name.trim(),
        company: newArchitect.company.trim() || null,
        telephone: newArchitect.telephone.trim() || null,
        email: newArchitect.email.trim() || null,
        address: newArchitect.address.trim() || null,
        postcode: newArchitect.postcode.trim().toUpperCase() || null,
        category: 'Architect',
        marketing_consent: 'Awaiting'
      });
      
      toast.success('Architect added successfully');
      setShowNewArchitectDialog(false);
      setNewArchitect({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
      
      // Refresh architects list and select the new one
      await fetchArchitects();
      setFormData(prev => ({ ...prev, architect_id: response.data.id }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create architect');
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.project_type) {
      newErrors.project_type = 'Project type is required';
    }
    
    if (!formData.client_name.trim()) {
      newErrors.client_name = 'Client name is required';
    }
    
    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = 'Address is required';
    }
    
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    } else if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(formData.postcode.trim())) {
      newErrors.postcode = 'Please enter a valid UK postcode';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        contact_id: formData.contact_id || null,
        architect_id: formData.architect_id || null
      };
      
      const response = await api.post('/projects', submitData);
      toast.success('Project created successfully');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Generate preview reference
  const previewRef = formData.project_type && formData.address_line1
    ? `LUX###-${formData.project_type}-${formData.address_line1}`
    : 'LUX###-[TYPE]-[Address]';

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/projects')}
          data-testid="back-btn"
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 
          className="text-2xl md:text-3xl font-bold text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          New Project
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Type Selection */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-[#F5A623]" />
              Project Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label className="text-gray-300">Select Type *</Label>
              <Select value={formData.project_type} onValueChange={(v) => handleChange('project_type', v)}>
                <SelectTrigger 
                  className={`bg-[#1E2530] border-white/20 text-white ${errors.project_type ? 'border-red-500' : ''}`}
                  data-testid="project-type-select"
                >
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {PROJECT_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-white focus:bg-[#F5A623]/20 focus:text-white">
                      {option.value} - {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.project_type && <p className="text-red-400 text-xs">{errors.project_type}</p>}
              <p className="text-xs text-gray-500">DEV = Development | CON = Construction</p>
            </div>
            
            {/* Project Source */}
            <div className="space-y-2 mt-4 pt-4 border-t border-white/10">
              <Label className="text-gray-300">Project Source *</Label>
              <Select value={formData.project_source} onValueChange={handleProjectSourceChange}>
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="project-source-select"
                >
                  <SelectValue placeholder="Select project source" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {PROJECT_SOURCE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-white focus:bg-[#F5A623]/20 focus:text-white">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {formData.project_source === 'Main Contractor' 
                  ? 'Select or create a contractor contact to link this project' 
                  : 'A client contact will be auto-created from the details below'}
              </p>
            </div>
            
            {/* Contractor Selection (only for Main Contractor) */}
            {formData.project_source === 'Main Contractor' && (
              <div className="space-y-2 mt-4">
                <Label className="text-gray-300">Select Contractor</Label>
                <Select 
                  value={formData.contact_id || 'none'} 
                  onValueChange={(v) => {
                    if (v === 'add_new') {
                      setTimeout(() => setShowNewContactDialog(true), 100);
                    } else {
                      handleContractorChange(v === 'none' ? '' : v);
                    }
                  }}
                >
                  <SelectTrigger 
                    className="bg-[#1E2530] border-white/20 text-white"
                    data-testid="contractor-select"
                  >
                    <SelectValue placeholder={loadingContractors ? "Loading contractors..." : "Select a contractor (optional)"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                    <SelectItem value="none" className="text-gray-400 focus:bg-[#F5A623]/20 focus:text-white">
                      No contractor selected
                    </SelectItem>
                    <SelectItem value="add_new" className="text-[#F5A623] focus:bg-[#F5A623]/20 focus:text-white font-medium">
                      + Add New Contractor
                    </SelectItem>
                    {contractors.map(contractor => (
                      <SelectItem 
                        key={contractor.id} 
                        value={contractor.id} 
                        className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                      >
                        {contractor.company || contractor.name}
                        {contractor.company && contractor.name !== contractor.company && (
                          <span className="text-gray-400 ml-2">({contractor.name})</span>
                        )}
                        {contractor.success_rate !== null && contractor.success_rate !== undefined && (
                          <span className={`ml-2 text-xs ${
                            contractor.success_rate >= 50 ? 'text-green-400' : 
                            contractor.success_rate >= 25 ? 'text-amber-400' : 
                            'text-gray-400'
                          }`}>
                            ({contractor.success_rate}% success)
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Architect Selection */}
            <div className="space-y-2 mt-4 pt-4 border-t border-white/10">
              <Label className="text-gray-300">Architect</Label>
              <Select 
                value={formData.architect_id || 'na'} 
                onValueChange={(v) => {
                  if (v === 'add_new') {
                    setTimeout(() => setShowNewArchitectDialog(true), 100);
                  } else {
                    setFormData(prev => ({ ...prev, architect_id: v === 'na' ? '' : v }));
                  }
                }}
              >
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="architect-select"
                >
                  <SelectValue placeholder={loadingArchitects ? "Loading architects..." : "Select an architect"} />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                  <SelectItem value="na" className="text-gray-400 focus:bg-[#F5A623]/20 focus:text-white">
                    N/A - No architect
                  </SelectItem>
                  <SelectItem value="add_new" className="text-[#F5A623] focus:bg-[#F5A623]/20 focus:text-white font-medium">
                    + Add New Architect
                  </SelectItem>
                  {architects.map(architect => (
                    <SelectItem 
                      key={architect.id} 
                      value={architect.id} 
                      className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                    >
                      {architect.company || architect.name}
                      {architect.company && architect.name !== architect.company && (
                        <span className="text-gray-400 ml-2">({architect.name})</span>
                      )}
                      {architect.success_rate !== null && architect.success_rate !== undefined && (
                        <span className={`ml-2 text-xs ${
                          architect.success_rate >= 50 ? 'text-green-400' : 
                          architect.success_rate >= 25 ? 'text-amber-400' : 
                          'text-gray-400'
                        }`}>
                          ({architect.success_rate}% success)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Optional: Link an architect to this project for tracking.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Client & Contact Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#F5A623]" />
              {formData.project_source === 'Main Contractor' ? 'Project Contact Information' : 'Client Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">
                {formData.project_source === 'Main Contractor' ? 'Project/Client Name *' : 'Client Name *'}
              </Label>
              <Input
                value={formData.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                placeholder={formData.project_source === 'Main Contractor' ? "Project name or end client" : "John Smith"}
                data-testid="client-name-input"
                className={`bg-[#1E2530] border-white/20 text-white ${errors.client_name ? 'border-red-500' : ''}`}
              />
              {errors.client_name && <p className="text-red-400 text-xs">{errors.client_name}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telephone
                </Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  placeholder="07700 900000"
                  data-testid="telephone-input"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="client@example.com"
                  data-testid="email-input"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-[#F5A623]" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Address Line 1 *</Label>
              <Input
                value={formData.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
                placeholder="e.g., 42 Victoria Street"
                data-testid="address-input"
                className={`bg-[#1E2530] border-white/20 text-white ${errors.address_line1 ? 'border-red-500' : ''}`}
              />
              {errors.address_line1 && <p className="text-red-400 text-xs">{errors.address_line1}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Town/City</Label>
                <Input
                  value={formData.town_city}
                  onChange={(e) => handleChange('town_city', e.target.value)}
                  placeholder="Northampton"
                  data-testid="town-city-input"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Postcode *</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
                  placeholder="e.g., NN1 3AB"
                  data-testid="postcode-input"
                  className={`bg-[#1E2530] border-white/20 text-white uppercase ${errors.postcode ? 'border-red-500' : ''}`}
                />
                {errors.postcode && <p className="text-red-400 text-xs">{errors.postcode}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status & Notes */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <ClipboardList className="w-5 h-5 text-[#F5A623]" />
              Status & Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="status-select"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {STATUS_OPTIONS.map(status => (
                    <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20 focus:text-white">
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Add any internal notes about this project..."
                rows={4}
                data-testid="notes-input"
                className="bg-[#1E2530] border-white/20 text-white resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="p-4 bg-[#2A3240] rounded-lg border border-white/10">
          <p className="text-xs text-gray-500 mb-1">Project Reference Preview:</p>
          <p 
            className="text-lg font-bold text-[#F5A623]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            data-testid="project-ref-preview"
          >
            {previewRef}
          </p>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/projects')}
            className="flex-1 border-white/20 text-gray-300 hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            data-testid="create-project-btn"
            className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Project
              </>
            )}
          </Button>
        </div>
      </form>

      {/* New Contractor Dialog */}
      <Dialog open={showNewContactDialog} onOpenChange={setShowNewContactDialog}>
        <DialogContent 
          className="bg-[#2A3240] border-white/10 text-white max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#F5A623]" />
              Add New Contractor
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new contractor contact to link to this project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-contractor-name"
                autoFocus
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Company</Label>
              <Input
                value={newContact.company}
                onChange={(e) => setNewContact(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-contractor-company"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Telephone</Label>
                <Input
                  value={newContact.telephone}
                  onChange={(e) => setNewContact(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="Phone number"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-contractor-telephone"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-contractor-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Address</Label>
              <Input
                value={newContact.address}
                onChange={(e) => setNewContact(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street address"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-contractor-address"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Postcode</Label>
              <Input
                value={newContact.postcode}
                onChange={(e) => setNewContact(prev => ({ ...prev, postcode: e.target.value }))}
                placeholder="NN1 1AA"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-contractor-postcode"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowNewContactDialog(false);
                setNewContact({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
              }}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateContact}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="create-contractor-submit"
            >
              Add Contractor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Architect Dialog */}
      <Dialog open={showNewArchitectDialog} onOpenChange={setShowNewArchitectDialog}>
        <DialogContent 
          className="bg-[#2A3240] border-white/10 text-white max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#F5A623]" />
              Add New Architect
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new architect contact to link to this project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Name *</Label>
              <Input
                value={newArchitect.name}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-name"
                autoFocus
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Company</Label>
              <Input
                value={newArchitect.company}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Architecture firm name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-company"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Telephone</Label>
                <Input
                  value={newArchitect.telephone}
                  onChange={(e) => setNewArchitect(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="Phone number"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-architect-telephone"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={newArchitect.email}
                  onChange={(e) => setNewArchitect(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-architect-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Address</Label>
              <Input
                value={newArchitect.address}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street address"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-address"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Postcode</Label>
              <Input
                value={newArchitect.postcode}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, postcode: e.target.value }))}
                placeholder="NN1 1AA"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-postcode"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowNewArchitectDialog(false);
                setNewArchitect({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
              }}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateArchitect}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="create-architect-submit"
            >
              Add Architect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
